import { REACT_APP_COUNTRY_DEPLOY, REACT_APP_FLYWIRE_CODE_OTHER, REACT_APP_FLYWIRE_CODE_US, REACT_APP_RULE_OMIT_PAYMENT_AMOUNT_ANUALYZED, REACT_APP_RULE_OMIT_PAYMENT_COUNTRY_EXCEPTION } from "config";
import { canOmitPayment, getAnnualizedPremium, isChargedUpfront } from "../payment/rules";

interface SessionFormData {
    firstName: string;
    middle_name: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    country: string;
    state: string;
    zipCode: string;
    age: number;
  }
  
interface SessionResponse {
    action: string;
    showOmitPayment: boolean;
    language: string;
    idQuotation: string;
    idPerson: string;
    typePerson: string;
    idApplication: string;
    frequency: string;
    amount: number;
    currency: string;
    automaticApproval: boolean;
    formData: SessionFormData;
    redirectURL: string;
    subscriptionHash: string;
    insuredAmount: number;
    clientId: string;
    companyCode: string;
    gateway?: 'flywire' | 'stripe' | 'authnet';
  }

  interface TransformedData {
    policyOwner: string | undefined;
    policyNumber: string | undefined;
    paymentFrequency: string | undefined;
    firstName: string | undefined;
    middleName: string | undefined;
    lastName: string | undefined;
    address: string | undefined;
    city: string | undefined;
    email: string | undefined;
    phone: string | undefined;
    amountStart: number | undefined;
    amount: number | undefined;
    country: string | undefined;
    state: string | undefined;
    zipCode?: string;
    currency: string | undefined;
    currencySymbol: string | undefined;
    idQuotation: string | undefined;
    idApplication: string | undefined;
    automaticApproval: boolean | undefined;
    showOmitPayment?: boolean;
    recurrent?: boolean;
    language?: string;
    idPerson?: string;
    frequency?: string;
    typeRecurrency?: string;
    payingForPolicy?: string;
    relationship?: string;
    recipientCode?: string;
    redirectUrl?: string;
    age?: number;
    insuredAmount?: number;
    needInterview?: boolean;
    typeIntervew?: string;
    clientId?: string;
    companyCode?: string;
    gateway?: 'flywire' | 'stripe' | 'authnet';
}


  function showOmitPayment(sessionResponse: SessionResponse): boolean {
    return canOmitPayment({
      market: REACT_APP_COUNTRY_DEPLOY,
      country: sessionResponse?.formData?.country || '',
      annualizedPremium: getAnnualizedPremium(sessionResponse.amount, sessionResponse.frequency),
      frequency: sessionResponse.frequency,
      automaticApproval: sessionResponse.automaticApproval,
      omitThreshold: REACT_APP_RULE_OMIT_PAYMENT_AMOUNT_ANUALYZED,
      countryException: REACT_APP_RULE_OMIT_PAYMENT_COUNTRY_EXCEPTION,
    });
  }
  function getTypeRecurrency(sessionResponse: SessionResponse): string {
    if (sessionResponse.frequency === 'M' || sessionResponse.frequency === 'Q') {
      return 'tokenization'
    }
    return 'optional_tokenization'
  }

  const getTypeExam = (insuredAmount:number, age:number) => {
    let type = 'v'
    if ((age > 45 && age <= 55) && (insuredAmount >= 760000 && insuredAmount <= 1000000)) {
      type ='m';
    }
    if ((age > 55 && age <= 65) && (insuredAmount >= 510000 && insuredAmount <= 750000)) {
      type ='m';
    }
    if ((age > 65 && age <= 75) && (insuredAmount >= 360000 && insuredAmount <= 500000)) {
      type ='m';
    }
    return type;
  }

  const needInterview = (age:number, insuredAmount:number, region: string) => {
    //console.log('needInterview::params==>', insuredAmount, age, region);
    if (region === 'BR') {
      return false; // In Brazil, no interview is needed
    }
    if (insuredAmount && age) {
      if ((age >= 18 && age <= 45) && (insuredAmount >= 760000 && insuredAmount <= 1000000)) {
        return true;
      }
      if (age > 45 && age <= 55 && (insuredAmount >= 510000 && insuredAmount <= 750000)) {
        return true;
      }
      if (age > 55 && age <= 65 && (insuredAmount >= 360000 && insuredAmount <= 500000)) {
        return true;
      }
      if (age > 65 && age <= 75 && (insuredAmount >= 100000 && insuredAmount <= 350000)) {
        return true;
      }
      return false;
    } else {
      console.warn('activity', 'Missing data for interview');
      return false;
    }
  }

  function transformObjectResponse(sessionResponse: SessionResponse, region: string): TransformedData {
    //console.log(sessionResponse)
    let flywireCode = REACT_APP_FLYWIRE_CODE_OTHER;
    if (sessionResponse?.formData.country === 'US') {
      flywireCode = REACT_APP_FLYWIRE_CODE_US
    }

    // El monto de la sesión es la prima (dato informativo). Lo que se le pasa a la
    // pasarela es lo que se cobra AHORA: 0 cuando el caso es de registro de tarjeta.
    // Misma condición que gobierna el recurringType en resolveRecurrence.
    const amount = isChargedUpfront(region, sessionResponse?.automaticApproval)
      ? (sessionResponse?.amount || 0)
      : 0;

    //console.log('needInterview', needInterview(sessionResponse?.formData.age || 0, sessionResponse?.insuredAmount || 0, region));

    const transformedData: TransformedData = {
      amount: amount,
      currencySymbol: '$',
      policyOwner: `${sessionResponse?.formData.firstName + ' ' + sessionResponse?.formData.lastName}`,
      policyNumber: sessionResponse?.idApplication,
      paymentFrequency: sessionResponse?.frequency || '',
      firstName: sessionResponse?.formData.firstName || '',
      middleName: sessionResponse?.formData.middle_name || '',
      lastName: sessionResponse?.formData.lastName || '',
      address: sessionResponse?.formData.address || '',
      city: sessionResponse?.formData.city || '',
      email: sessionResponse?.formData.email || '',
      phone: sessionResponse?.formData.phone || '',
      amountStart: sessionResponse?.amount || 0,
      country: sessionResponse?.formData.country || '',
      idQuotation: sessionResponse?.idQuotation || '',
      idApplication: sessionResponse?.idApplication || '',
      automaticApproval: sessionResponse?.automaticApproval || false,
      showOmitPayment: showOmitPayment(sessionResponse),
      recurrent: true,
      language: sessionResponse?.language || '',
      idPerson: sessionResponse?.idPerson || '',
      currency: sessionResponse?.currency || '',
      state: sessionResponse?.formData.state || '',
      zipCode: sessionResponse?.formData.zipCode || '',
      frequency: sessionResponse?.frequency || '',
      typeRecurrency: getTypeRecurrency(sessionResponse),
      payingForPolicy: '1',
      relationship: '',
      recipientCode: flywireCode,
      redirectUrl: sessionResponse?.redirectURL || '',
      age: sessionResponse?.formData.age || undefined,
      insuredAmount: sessionResponse?.insuredAmount || undefined,
      needInterview: needInterview(sessionResponse?.formData.age || 0, sessionResponse?.insuredAmount || 0, region),
      typeIntervew: getTypeExam(sessionResponse?.insuredAmount || 0, sessionResponse?.formData.age || 0),
      clientId: sessionResponse?.clientId || '',
      companyCode: sessionResponse?.companyCode || '',
      gateway: sessionResponse?.gateway
    };
    return transformedData;
}

export type { SessionResponse, SessionFormData };
export { transformObjectResponse };